// src/pages/CustomerAccount/pages/Components/TabContents.jsx
import React, { useState, useEffect } from 'react';
import { Edit2, Plus, Heart, Wallet, Award, Headphones, MapPin, Home, Hash, Building, Phone, CheckCircle, X, Trash2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { COLORS } from './CustomerAccount.styles';
import OrdersTable from './OrdersTable';
import EditModal from './EditModal';
import { getAddresses, addAddress, updateAddress, deleteAddress } from '../../../../services/addressServices';
import { getMyOrderList } from '../../../../redux/features/Order/orderThunk';
import Loader from '../../../../components/Loader';
import Message from '../../../../components/Message';

// ----- لیست استان‌های ایران -----
const IRAN_PROVINCES = [
  'آذربایجان شرقی', 'آذربایجان غربی', 'اردبیل', 'اصفهان', 'البرز',
  'ایلام', 'بوشهر', 'تهران', 'چهارمحال و بختیاری', 'خراسان جنوبی',
  'خراسان رضوی', 'خراسان شمالی', 'خوزستان', 'زنجان', 'سمنان',
  'سیستان و بلوچستان', 'فارس', 'قزوین', 'قم', 'کردستان',
  'کرمان', 'کرمانشاه', 'کهگیلویه و بویراحمد', 'گلستان', 'گیلان',
  'لرستان', 'مازندران', 'مرکزی', 'هرمزگان', 'همدان', 'یزد'
];

// ============================================================
// ===== تب سفارشات (با تبدیل داده‌ها به فرمت جدول) ==============
// ============================================================
export const OrdersContent = ({ navigate }) => {
  const dispatch = useDispatch();
  const { orders: rawOrders, loading, error } = useSelector((state) => state.orderListMy) || { orders: [], loading: false, error: '' };
  
  // ✅ اصلاح: استفاده از auth به جای userLogin
  const { userInfo } = useSelector((state) => state.auth) || {};

  useEffect(() => {
    if (userInfo?.access) {
      dispatch(getMyOrderList());
    }
  }, [dispatch, userInfo]);

  // ✅ مرتب‌سازی: جدیدترین سفارش در بالا
  const sortedOrders = (rawOrders || [])
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const mappedOrders = sortedOrders.map(order => ({
    id: `#${order._id}`,
    date: new Date(order.createdAt).toLocaleDateString('fa-IR'),
    total: `${order.totalPrice?.toLocaleString()} تومان`,
    paid: order.isPaid ? 'بله' : 'خیر',
    delivered: order.isDelivered ? 'تحویل شده' : (order.isPaid ? 'در حال ارسال' : 'در انتظار'),
    status: order.isDelivered ? 'delivered' : (order.isPaid ? 'processing' : 'pending'),
    _id: order._id,
  }));

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <Message variant="danger" text={error} />
        <button
          onClick={() => dispatch(getMyOrderList())}
          className="mt-4 px-4 py-2 bg-[#9EAD8C] text-white rounded-lg hover:bg-[#8A9A7B] transition"
        >
          تلاش مجدد
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold sm:text-xl" style={{ color: COLORS.text }}>سفارشات</h2>
      {mappedOrders.length > 0 ? (
        <OrdersTable
          orders={mappedOrders}
          onViewDetails={(order) => {
            if (order._id) {
              navigate(`/order/${order._id}`);
            } else {
              console.warn('سفارش فاقد شناسه است:', order);
            }
          }}
        />
      ) : (
        <div className="text-center py-8 text-gray-500">هیچ سفارشی یافت نشد.</div>
      )}
    </div>
  );
};

// ============================================================
// ===== کامپوننت آدرس‌ها با دیزاین جدید =========================
// ============================================================
export const AddressesContent = ({
  userInfo,
  shippingAddress,
  onEdit,
  isEditing,
  editData,
  onSave,
  onCancel,
  showAddAddress,
  setShowAddAddress
}) => {
  const dispatch = useDispatch();
  
  // ✅ اصلاح: استفاده از auth به جای userLogin
  const { userInfo: userInfoFromRedux } = useSelector((state) => state.auth) || {};

  const [addressList, setAddressList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newAddress, setNewAddress] = useState({
    address: '',
    postal_code: '',
    city: '',
    province: '',
    phone: '',
    plaque: '',
    unit: ''
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const validatePhone = (phone) => /^[0-9]{11}$/.test(phone);

  const fetchAddresses = async () => {
    if (!userInfoFromRedux?.access) return;
    setLoading(true);
    try {
      const res = await getAddresses({ userInfo: userInfoFromRedux, dispatch });
      setAddressList(res.data);
    } catch (err) {
      console.error('خطا در دریافت آدرس‌ها:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, [userInfoFromRedux]);

  const handleAddAddress = async () => {
    if (newAddress.phone && !validatePhone(newAddress.phone)) {
      setPhoneError('شماره تلفن باید ۱۱ رقم باشد');
      return;
    }
    setPhoneError('');

    if (!newAddress.address || !newAddress.city || !newAddress.province) {
      alert('لطفاً آدرس، شهر و استان را وارد کنید!');
      return;
    }

    try {
      const res = await addAddress(newAddress, { userInfo: userInfoFromRedux, dispatch });
      setAddressList([...addressList, res.data]);
      setNewAddress({ address: '', postal_code: '', city: '', province: '', phone: '', plaque: '', unit: '' });
      setShowAddAddress(false);
      onSave({ ...userInfo });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'خطا در ثبت آدرس';
      alert('خطا در ثبت آدرس: ' + errorMsg);
    }
  };

  const handleDeleteAddress = async (index) => {
    const addr = addressList[index];
    if (addr.is_default) {
      alert('آدرس پیش‌فرض قابل حذف نیست.');
      return;
    }
    if (!window.confirm('آیا از حذف این آدرس مطمئن هستید؟')) return;
    try {
      await deleteAddress(addr.id, { userInfo: userInfoFromRedux, dispatch });
      setAddressList(addressList.filter((_, i) => i !== index));
    } catch (err) {
      alert('خطا در حذف آدرس: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditAddress = (index) => {
    setEditingIndex(index);
    setEditingAddress({ ...addressList[index] });
    setIsAddressEditing(true);
    setPhoneError('');
  };

  const handleSaveAddressEdit = async () => {
    if (editingAddress.phone && !validatePhone(editingAddress.phone)) {
      setPhoneError('شماره تلفن باید ۱۱ رقم باشد');
      return;
    }
    setPhoneError('');

    if (!editingAddress.address || !editingAddress.city || !editingAddress.province) {
      alert('آدرس، شهر و استان الزامی هستند!');
      return;
    }

    try {
      const res = await updateAddress(editingAddress.id, editingAddress, { userInfo: userInfoFromRedux, dispatch });
      const updatedList = [...addressList];
      updatedList[editingIndex] = res.data;
      setAddressList(updatedList);
      setIsAddressEditing(false);
      setEditingIndex(null);
      setEditingAddress(null);
      onSave({ ...userInfo });
    } catch (err) {
      alert('خطا در ویرایش آدرس: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancelAddressEdit = () => {
    setIsAddressEditing(false);
    setEditingIndex(null);
    setEditingAddress(null);
    setPhoneError('');
  };

  // استایل‌های مشترک برای ورودی‌ها
  const inputBaseClass = "w-full rounded-xl border-2 bg-gray-50/50 px-4 py-3 text-sm outline-none transition-all duration-200 hover:bg-white focus:bg-white focus:ring-2 focus:ring-opacity-50 focus:border-transparent placeholder:text-gray-400";
  const inputStyle = {
    borderColor: COLORS.border,
    '--tw-ring-color': `${COLORS.primary}40`,
    color: COLORS.text
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div 
            className="rounded-2xl p-2.5"
            style={{ backgroundColor: `${COLORS.primary}15` }}
          >
            <MapPin className="h-5 w-5" style={{ color: COLORS.primary }} />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ color: COLORS.text }}>
              آدرس‌های من
            </h2>
            <p className="text-sm" style={{ color: COLORS.textLight }}>
              {addressList.length} آدرس ثبت شده
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAddAddress(v => !v)}
          className="group flex items-center gap-2 rounded-2xl px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
            boxShadow: `0 4px 15px ${COLORS.primary}30`
          }}
        >
          <Plus className={`h-4 w-4 transition-transform duration-200 ${showAddAddress ? 'rotate-45' : 'group-hover:rotate-90'}`} />
          {showAddAddress ? 'بستن فرم' : 'افزودن آدرس جدید'}
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-transparent" style={{ borderColor: COLORS.primary, borderTopColor: 'transparent' }} />
          <p className="mt-4 text-sm" style={{ color: COLORS.primaryLight }}>در حال بارگذاری آدرس‌ها...</p>
        </div>
      ) : (
        <>
          {/* Add Address Form */}
          {showAddAddress && (
            <div 
              className="overflow-hidden rounded-2xl border-2 p-6 transition-all duration-300"
              style={{ 
                background: COLORS.cardBg, 
                borderColor: COLORS.border,
                boxShadow: `0 4px 20px ${COLORS.primary}10`
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="rounded-full p-1.5" style={{ backgroundColor: `${COLORS.primary}20` }}>
                  <Plus className="h-4 w-4" style={{ color: COLORS.primary }} />
                </div>
                <h4 className="font-semibold" style={{ color: COLORS.text }}>ثبت آدرس جدید</h4>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                    آدرس <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newAddress.address}
                    onChange={e => setNewAddress({ ...newAddress, address: e.target.value })}
                    rows={3}
                    className={`${inputBaseClass} resize-none`}
                    style={inputStyle}
                    placeholder="خیابان، محله، کوچه، پلاک..."
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                      استان <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newAddress.province}
                      onChange={e => setNewAddress({ ...newAddress, province: e.target.value })}
                      className={`${inputBaseClass} appearance-none`}
                      style={inputStyle}
                    >
                      <option value="">انتخاب استان...</option>
                      {IRAN_PROVINCES.map(prov => (
                        <option key={prov} value={prov}>{prov}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                      شهر <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newAddress.city}
                      onChange={e => setNewAddress({ ...newAddress, city: e.target.value })}
                      className={inputBaseClass}
                      style={inputStyle}
                      placeholder="مثال: تهران"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                      کد پستی
                    </label>
                    <input
                      type="text"
                      value={newAddress.postal_code}
                      onChange={e => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                      className={inputBaseClass}
                      style={inputStyle}
                      placeholder="۱۰ رقمی"
                    />
                  </div>
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                      پلاک
                    </label>
                    <input
                      type="text"
                      value={newAddress.plaque}
                      onChange={e => setNewAddress({ ...newAddress, plaque: e.target.value })}
                      className={inputBaseClass}
                      style={inputStyle}
                      placeholder="مثال: ۱۲۳"
                    />
                  </div>
                  <div>
                    <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                      واحد
                    </label>
                    <input
                      type="text"
                      value={newAddress.unit}
                      onChange={e => setNewAddress({ ...newAddress, unit: e.target.value })}
                      className={inputBaseClass}
                      style={inputStyle}
                      placeholder="مثال: ۵"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                    شماره تلفن همراه
                  </label>
                  <input
                    type="tel"
                    value={newAddress.phone}
                    onChange={e => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 11) {
                        setNewAddress({ ...newAddress, phone: value });
                        setPhoneError('');
                      }
                    }}
                    className={inputBaseClass}
                    style={inputStyle}
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                    maxLength={11}
                  />
                  {phoneError && (
                    <p className="mt-2 flex items-center gap-1 text-xs text-red-500">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                      {phoneError}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleAddAddress}
                  className="group w-full rounded-2xl py-3 text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
                  style={{ 
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                    boxShadow: `0 4px 15px ${COLORS.primary}30`
                  }}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Plus className="h-4 w-4 transition-transform duration-200 group-hover:rotate-90" />
                    ثبت آدرس جدید
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Address List */}
          {addressList.length > 0 ? (
            <div className="space-y-4">
              {addressList.map((addr, idx) => (
                <div 
                  key={addr.id || idx} 
                  className="group rounded-2xl border-2 p-5 transition-all duration-200 hover:shadow-lg"
                  style={{ 
                    background: COLORS.cardBg, 
                    borderColor: addr.is_default ? COLORS.primary : COLORS.border,
                    boxShadow: addr.is_default ? `0 4px 20px ${COLORS.primary}15` : 'none'
                  }}
                >
                  {isAddressEditing && editingIndex === idx ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="rounded-full p-1.5" style={{ backgroundColor: `${COLORS.primary}20` }}>
                          <Edit2 className="h-4 w-4" style={{ color: COLORS.primary }} />
                        </div>
                        <h4 className="font-semibold" style={{ color: COLORS.text }}>ویرایش آدرس</h4>
                      </div>

                      <div>
                        <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                          آدرس <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={editingAddress.address}
                          onChange={e => setEditingAddress({ ...editingAddress, address: e.target.value })}
                          rows={3}
                          className={`${inputBaseClass} resize-none`}
                          style={inputStyle}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                            استان <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={editingAddress.province}
                            onChange={e => setEditingAddress({ ...editingAddress, province: e.target.value })}
                            className={`${inputBaseClass} appearance-none`}
                            style={inputStyle}
                          >
                            <option value="">انتخاب استان...</option>
                            {IRAN_PROVINCES.map(prov => (
                              <option key={prov} value={prov}>{prov}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                            شهر <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editingAddress.city}
                            onChange={e => setEditingAddress({ ...editingAddress, city: e.target.value })}
                            className={inputBaseClass}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                          <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                            کد پستی
                          </label>
                          <input
                            type="text"
                            value={editingAddress.postal_code}
                            onChange={e => setEditingAddress({ ...editingAddress, postal_code: e.target.value })}
                            className={inputBaseClass}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                            پلاک
                          </label>
                          <input
                            type="text"
                            value={editingAddress.plaque}
                            onChange={e => setEditingAddress({ ...editingAddress, plaque: e.target.value })}
                            className={inputBaseClass}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                            واحد
                          </label>
                          <input
                            type="text"
                            value={editingAddress.unit}
                            onChange={e => setEditingAddress({ ...editingAddress, unit: e.target.value })}
                            className={inputBaseClass}
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 flex items-center gap-1.5 text-sm font-medium" style={{ color: COLORS.text }}>
                          شماره تلفن همراه
                        </label>
                        <input
                          type="tel"
                          value={editingAddress.phone}
                          onChange={e => {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 11) {
                              setEditingAddress({ ...editingAddress, phone: value });
                              setPhoneError('');
                            }
                          }}
                          className={inputBaseClass}
                          style={inputStyle}
                          maxLength={11}
                        />
                        {phoneError && (
                          <p className="mt-2 flex items-center gap-1 text-xs text-red-500">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                            {phoneError}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={handleSaveAddressEdit}
                          className="flex-1 rounded-2xl py-2.5 text-sm font-medium text-white transition-all duration-200 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
                          style={{ 
                            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                            boxShadow: `0 4px 15px ${COLORS.primary}30`
                          }}
                        >
                          ذخیره تغییرات
                        </button>
                        <button
                          onClick={handleCancelAddressEdit}
                          className="flex-1 rounded-2xl border-2 bg-white py-2.5 text-sm font-medium transition-all duration-200 hover:bg-gray-50 active:scale-95"
                          style={{ borderColor: COLORS.border, color: COLORS.text }}
                        >
                          انصراف
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <>
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold" style={{ color: COLORS.text }}>
                              آدرس {idx + 1}
                            </p>
                            {addr.is_default && (
                              <span 
                                className="inline-flex items-center gap-1 rounded-full border-2 px-2.5 py-0.5 text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${COLORS.primary}15`,
                                  borderColor: COLORS.primary,
                                  color: COLORS.primary
                                }}
                              >
                                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: COLORS.primary }} />
                                آدرس پیش‌فرض
                              </span>
                            )}
                          </div>
                          <div className="mt-2 space-y-1">
                            <p className="text-sm leading-relaxed" style={{ color: COLORS.text }}>
                              {addr.address}
                            </p>
                            <p className="text-sm" style={{ color: COLORS.primaryLight }}>
                              {addr.city}، {addr.province}
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm" style={{ color: COLORS.primaryLight }}>
                              {addr.postal_code && <span>کد پستی: {addr.postal_code}</span>}
                              {addr.plaque && <span>پلاک: {addr.plaque}</span>}
                              {addr.unit && <span>واحد: {addr.unit}</span>}
                              {addr.phone && <span>تلفن: {addr.phone}</span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: COLORS.border }}>
                        <button
                          onClick={() => handleEditAddress(idx)}
                          className="group flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-100 active:scale-95"
                          style={{ color: COLORS.primary }}
                        >
                          <Edit2 className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                          ویرایش
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(idx)}
                          className={`group flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 ${
                            addr.is_default ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50 active:scale-95'
                          }`}
                          style={{ color: addr.is_default ? COLORS.primaryLight : COLORS.danger }}
                          disabled={addr.is_default}
                        >
                          <Trash2 className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                          حذف
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed py-16" style={{ borderColor: COLORS.border }}>
              <div className="rounded-full p-4" style={{ backgroundColor: `${COLORS.primary}10` }}>
                <MapPin className="h-10 w-10" style={{ color: COLORS.primaryLight }} />
              </div>
              <p className="mt-4 text-center" style={{ color: COLORS.primaryLight }}>
                <span className="block text-base font-medium" style={{ color: COLORS.text }}>
                  هیچ آدرسی ثبت نشده است
                </span>
                <span className="mt-1 block text-sm">
                  برای افزودن آدرس جدید، دکمه "افزودن آدرس جدید" را بزنید.
                </span>
              </p>
            </div>
          )}
        </>
      )}

      {isEditing && (
        <EditModal
          userInfo={editData || userInfo}
          onSave={onSave}
          onCancel={onCancel}
          section={editData?.section}
        />
      )}
    </div>
  );
};

// ============================================================
// ===== سایر تب‌ها ===========================================
// ============================================================

export const WishlistContent = () => (
  <div className="space-y-4">
    <h2 className="text-lg font-bold sm:text-xl" style={{ color: COLORS.text }}>علاقه‌مندی‌ها</h2>
    <div className="py-12 text-center" style={{ color: COLORS.primaryLight }}>
      <Heart className="mx-auto mb-4 h-12 w-12 opacity-20" />
      <p className="text-sm sm:text-base font-medium">در حال توسعه!</p>
      <p className="text-sm opacity-75 mt-1">این بخش در آپدیت‌های آینده به فروشگاه اضافه خواهد شد.</p>
    </div>
  </div>
);

export const WalletContent = () => (
  <div className="space-y-4">
    <h2 className="text-lg font-bold sm:text-xl" style={{ color: COLORS.text }}>کیف پول من</h2>
    <div className="py-12 text-center" style={{ color: COLORS.primaryLight }}>
      <Wallet className="mx-auto mb-4 h-12 w-12 opacity-20" />
      <p className="text-sm sm:text-base font-medium">به زودی!</p>
      <p className="text-sm opacity-75 mt-1">ما در حال توسعه سیستم کیف پول هوشمند هستیم.</p>
    </div>
  </div>
);

export const MarketingContent = () => (
  <div className="space-y-4">
    <h2 className="text-lg font-bold sm:text-xl" style={{ color: COLORS.text }}>بازاریاب شوید</h2>
    <div className="py-12 text-center" style={{ color: COLORS.primaryLight }}>
      <Award className="mx-auto mb-4 h-12 w-12 opacity-20" />
      <p className="text-sm sm:text-base font-medium">در دست ساخت!</p>
      <p className="text-sm opacity-75 mt-1">سیستم بازاریابی و همکاری در فروش در حال طراحی است.</p>
    </div>
  </div>
);

export const SupportContent = () => (
  <div className="space-y-4 px-2 py-8 text-center sm:px-0">
    <Headphones className="mx-auto h-14 w-14 sm:h-16 sm:w-16" style={{ color: COLORS.primary }} />
    <h2 className="text-lg font-bold sm:text-xl" style={{ color: COLORS.text }}>پشتیبانی</h2>
    <p className="mx-auto max-w-md text-sm" style={{ color: COLORS.primaryLight }}>۲۴ ساعته، ۷ روز هفته در خدمت شما هستیم</p>
    <button
      className="rounded-xl px-6 py-3 text-sm font-medium text-white transition-all hover:shadow-lg sm:text-base"
      style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}
    >
      تماس با پشتیبانی
    </button>
  </div>
);